import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useSupabaseQuery } from './useSupabase';
import { useAppStore } from '../stores/appStore';
import type { AIAgent } from '../types';
import type { Database } from '../lib/database.types';

type AgentInsert = Database['public']['Tables']['ai_agents']['Insert'];
type AgentUpdate = Database['public']['Tables']['ai_agents']['Update'];

const MOCK_AGENTS: AIAgent[] = [
  { id: '1', org_id: 'demo', name: 'Luna', avatar: '🌙', role: 'Lead Qualifier', system_prompt: '', model: 'GPT-4o', temperature: 0.7, max_tokens: 1000, status: 'active', config: {}, created_at: new Date().toISOString() },
  { id: '2', org_id: 'demo', name: 'Atlas', avatar: '🗺️', role: 'Meeting Booker', system_prompt: '', model: 'Claude 3.5', temperature: 0.7, max_tokens: 1000, status: 'active', config: {}, created_at: new Date().toISOString() },
  { id: '3', org_id: 'demo', name: 'Nova', avatar: '⭐', role: 'Product Pitcher', system_prompt: '', model: 'GPT-4o', temperature: 0.7, max_tokens: 1000, status: 'paused', config: {}, created_at: new Date().toISOString() },
];

export function useAgents(): {
  agents: AIAgent[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  createAgent: (data: Omit<AgentInsert, 'org_id'>) => Promise<void>;
  updateAgent: (id: string, data: AgentUpdate) => Promise<void>;
  deleteAgent: (id: string) => Promise<void>;
} {
  const orgId = useAppStore(s => s.orgId);
  const [mutating, setMutating] = useState(false);

  const { data, loading, error, refetch } = useSupabaseQuery<AIAgent[]>(
    async () => {
      if (!isSupabaseConfigured) return { data: MOCK_AGENTS, error: null };
      const { data: rows, error: err } = await supabase
        .from('ai_agents')
        .select('*')
        .order('created_at', { ascending: false });
      return { data: rows as AIAgent[] | null, error: err };
    },
    []
  );

  const createAgent = useCallback(
    async (insertData: Omit<AgentInsert, 'org_id'>) => {
      if (!isSupabaseConfigured) {
        toast.info('Connect Supabase to create agents');
        return;
      }
      setMutating(true);
      try {
        const { error: err } = await supabase.from('ai_agents').insert({ ...insertData, org_id: orgId! });
        if (err) throw err;
        toast.success('Agent created');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create agent');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  const updateAgent = useCallback(
    async (id: string, updateData: AgentUpdate) => {
      if (!isSupabaseConfigured) {
        toast.info('Connect Supabase to update agents');
        return;
      }
      setMutating(true);
      try {
        const { error: err } = await supabase.from('ai_agents').update(updateData).eq('id', id);
        if (err) throw err;
        toast.success('Agent updated');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update agent');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  const deleteAgent = useCallback(
    async (id: string) => {
      if (!isSupabaseConfigured) {
        toast.info('Connect Supabase to delete agents');
        return;
      }
      setMutating(true);
      try {
        const { error: err } = await supabase.from('ai_agents').delete().eq('id', id);
        if (err) throw err;
        toast.success('Agent deleted');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to delete agent');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  return {
    agents: data ?? (isSupabaseConfigured ? [] : MOCK_AGENTS),
    loading: loading || mutating,
    error,
    refetch,
    createAgent,
    updateAgent,
    deleteAgent,
  };
}
